import React from 'react';
import { AccordionItem, Label, Input, TextArea, ControlError } from "@forge/common";
import { CREATE_COURSE_CONSTANTS } from "../utils/constants";
import { IGeneralDetails, IGeneralDetailsFormError } from "../model/index";

interface GeneralDetailsSectionProps {
    generalDetails: IGeneralDetails;
    generalDetailsFormError: IGeneralDetailsFormError;
    isEditMode: boolean;
    onFieldChange: (field: 'courseName' | 'description' | 'courseDuration', value: string) => void;
    onFieldBlur: (field: 'courseName' | 'description' | 'courseDuration') => void;
}

const GeneralDetailsSection: React.FC<GeneralDetailsSectionProps> = ({
    generalDetails,
    generalDetailsFormError,
    isEditMode,
    onFieldChange,
    onFieldBlur
}) => {
    return (
        <div className="grid grid-cols-3 xl-low:!gap-3 xl-mid:!gap-16 xl-high:!gap-16">
            {/* Course Name Section */}
            <div className="col-span-1">
                <div>
                    <Label required className="createCourse-label mb-2">
                        {CREATE_COURSE_CONSTANTS.GENERAL_DETAILS.COURSE_NAME.LABEL}
                    </Label>
                    <Input
                        id="course-name"
                        className={`createCourse-name-input-field ${!generalDetailsFormError.courseName ? '!border-none' : ''}`}
                        data-testid="course-name"
                        size="regular"
                        disabled={isEditMode}
                        placeholder={CREATE_COURSE_CONSTANTS.GENERAL_DETAILS.COURSE_NAME.PLACEHOLDER}
                        value={generalDetails.courseName}
                        onChange={(e) => onFieldChange('courseName', e.target.value)}
                        onBlur={() => onFieldBlur('courseName')}
                        error={generalDetailsFormError.courseName !== ''}
                        autoComplete="off"
                    />
                    {generalDetailsFormError.courseName === "" && (
                        <div className="text-sm text-gray-400 mt-1 text-xs">
                            Max 30 characters
                        </div>
                    )}
                    {generalDetailsFormError.courseName && (
                        <ControlError className="text-xs" showIcon={false}>
                            {generalDetailsFormError.courseName}
                        </ControlError>
                    )}
                </div>

                {/* Description Section */}
                <div className="mt-4">
                    <Label className="createCourse-label mb-2">
                        {CREATE_COURSE_CONSTANTS.GENERAL_DETAILS.DESCRIPTION.LABEL}
                    </Label>
                    <TextArea
                        id="course-description"
                        className={`createCourse-description-input-field no-resize ${!generalDetailsFormError.courseDescription ? '!border-none' : ''}`}
                        data-testid="course-description"
                        placeholder={CREATE_COURSE_CONSTANTS.GENERAL_DETAILS.DESCRIPTION.PLACEHOLDER}
                        rows={2}
                        size="regular"
                        value={generalDetails.courseDescription}
                        onChange={(e) => onFieldChange('description', e.target.value)}
                        onBlur={() => onFieldBlur('description')}
                        error={generalDetailsFormError.courseDescription !== ''}
                        autoComplete="off"
                    />
                    {generalDetailsFormError.courseDescription === "" && (
                        <div className="text-sm text-gray-400 mt-1 text-xs">
                            Max 250 characters
                        </div>
                    )}
                    {generalDetailsFormError.courseDescription && (
                        <ControlError className="text-xs" showIcon={false}>
                            {generalDetailsFormError.courseDescription}
                        </ControlError>
                    )}
                </div>
            </div>

            {/* Course Duration Section */}
            <div className="col-span-1">
                <div>
                    <Label required className="createCourse-label mb-2">
                        {CREATE_COURSE_CONSTANTS.GENERAL_DETAILS.COURSE_DURATION.LABEL}
                    </Label>
                    <Input
                        id="course-duration"
                        className={`createCourse-duration-input-field ${!generalDetailsFormError.courseDuration ? '!border-none' : ''}`}
                        data-testid="course-duration"
                        size="regular"
                        placeholder={CREATE_COURSE_CONSTANTS.GENERAL_DETAILS.COURSE_DURATION.PLACEHOLDER}
                        value={generalDetails.courseDuration ?? ''}
                        onChange={(e) => onFieldChange('courseDuration', e.target.value)}
                        onBlur={() => onFieldBlur('courseDuration')}
                        error={generalDetailsFormError.courseDuration !== ''}
                        autoComplete="off"
                    />
                    {generalDetailsFormError.courseDuration === '' && (
                        <div className="text-sm text-gray-400 mt-1 text-xs">
                            Only numerical numbers allowed
                        </div>
                    )}
                    {generalDetailsFormError.courseDuration && (
                        <ControlError className="text-xs" showIcon={false}>
                            {generalDetailsFormError.courseDuration}
                        </ControlError>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GeneralDetailsSection;